import { z } from 'zod';
import { CONTACT_METHODS, HEARD_FROM, SIGNATURE_MAX } from './questionnaire';
/**
 * Client form on a separate iPad (/anketa), not tied to a booking (owner 18.10.2026).
 *
 * Health data: the client READS the contraindications and answers only "none of these" or
 * "something applies — I will tell my therapist". WHICH condition is never asked, never stored.
 * Kept (encrypted, 24 months): name, birth date, e-mail, preferences, the outcome, the signature.
 * The client card in the CRM gets name, surname, phone, e-mail, birth date and one Note line
 * (owner 28.10.2026): «Form dd.mm.yyyy · No contraindications · Language · Pressure · Heard from · Comment».
 */
export const ANKETA_NAME_MAX = 40;
export const ANKETA_SURNAME_MAX = 60;
/** Free comment of the client at the end of the form (owner 28.10.2026) → the CRM card Note. */
export const ANKETA_COMMENT_MAX = 300;
const PhoneSchema = z
    .string()
    .transform((s) => s.replace(/[\s()-]/g, ''))
    .pipe(z.string().regex(/^\+?\d{9,15}$/, 'phone'));
export const AnketaRecognizeRequestSchema = z.object({ phone: PhoneSchema });
export const AnketaFormRequestSchema = z
    .object({
    /** Questionnaire version the client read (same contraindication list as the kiosk form). */
    version: z.string().regex(/^[a-z0-9-]{1,40}$/),
    /** UI language the client used (evidence of what was read). */
    locale: z.string().regex(/^[a-z]{2}$/),
    /** Required unless `recognized` (the name then comes from the client card on the server). */
    firstName: z.string().trim().min(1).max(ANKETA_NAME_MAX).optional(),
    lastName: z.string().trim().min(1).max(ANKETA_SURNAME_MAX).optional(),
    /**
     * «Is this you?» token (owner 28.10.2026): the phone of an online booking is already in the CRM,
     * the client confirmed «yes, it's me» — the form joins that card, no name typed, no duplicate.
     */
    recognized: z
        .string()
        .regex(/^[A-Za-z0-9_-]{16,64}$/)
        .optional(),
    phone: PhoneSchema,
    email: z.string().trim().toLowerCase().email().max(120).optional(),
    birthDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    contact: z.enum(CONTACT_METHODS).optional(),
    pressure: z.number().int().min(1).max(5).optional(),
    heardFrom: z.enum(HEARD_FROM).optional(),
    /** Outcome only: no list, no note. */
    /** `cleared`: something applies, but a doctor has cleared the treatment (owner 19.10.2026). */
    conditions: z.enum(['none', 'declared', 'cleared']),
    aesthetic: z.enum(['none', 'declared']),
    /** Anything the client wants the studio to know (owner 28.10.2026); one line in the CRM Note. */
    comment: z
        .string()
        .transform((t) => t.replace(/\s+/g, ' ').trim())
        .pipe(z.string().max(ANKETA_COMMENT_MAX))
        .optional(),
    /** Truth of the answers + explicit consent (GDPR art. 9(2)(a)). */
    consent: z.literal(true),
    /** Privacy notice read; a client card is created in the studio's booking system. */
    privacy: z.literal(true),
    marketing: z.boolean().default(false),
    signature: z
        .string()
        .max(SIGNATURE_MAX)
        .regex(/^data:image\/png;base64,[A-Za-z0-9+/]+=*$/),
})
    .refine((f) => f.recognized || (f.firstName && f.lastName), { path: ['firstName'], message: 'required' });
/** «Where did you hear about us» as written in the CRM Note (in English, owner 28.10.2026). */
const HEARD_EN = {
    instagram: 'Instagram',
    tiktok: 'TikTok',
    google: 'Google',
    ai_assistant: 'AI assistant',
    friend: 'friend',
    classpass: 'ClassPass',
    passing_by: 'passing by',
    other: 'other',
};
/** The form language in English, so the team knows which language to speak. */
const LANGUAGE_EN = {
    pt: 'Portuguese',
    en: 'English',
    fr: 'French',
    es: 'Spanish',
    ru: 'Russian',
    uk: 'Ukrainian',
    de: 'German',
};
/**
 * The one line the iPad form adds to the CRM card Note (owner 28.10.2026), in English, e.g.
 * «Form 28.10.2026 · No contraindications · Language: Russian · Pressure: 3/5 · Heard from: Google
 * · Comment: …». The comment stays as the client wrote it. Only the outcome the client confirmed —
 * never which condition (the form does not ask).
 */
export function anketaNoteLine(date, form) {
    const [y, m, d] = date.split('-');
    const health = form.conditions === 'none' && form.aesthetic === 'none'
        ? 'No contraindications'
        : form.conditions === 'cleared'
            ? 'Contraindication cleared by a doctor — talk to the client'
            : 'Contraindication declared — talk to the client before the treatment';
    const parts = [`Form ${d}.${m}.${y}`, health];
    const lang = form.locale ? (LANGUAGE_EN[form.locale] ?? form.locale) : undefined;
    if (lang)
        parts.push(`Language: ${lang}`);
    if (form.pressure)
        parts.push(`Pressure: ${form.pressure}/5`);
    if (form.heardFrom)
        parts.push(`Heard from: ${HEARD_EN[form.heardFrom]}`);
    const comment = form.comment?.replace(/\s+/g, ' ').trim();
    if (comment)
        parts.push(`Comment: ${comment}`);
    return parts.join(' · ');
}
//# sourceMappingURL=anketa.js.map